import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { gridToHtml, jsonToGridData } from './utils/signatureToHtml';
import { SignatureDynamicData } from './blocks/types';
import { Card, CardContent } from '@/components/ui/card';
import { Info } from 'lucide-react';

interface SignaturePreviewProps {
  userProfile: {
    first_name?: string;
    last_name?: string;
    company_position?: string;
    personal_phone_number?: string;
    email?: string;
    profile_photo_url?: string;
  };
  company?: {
    name?: string;
    email?: string;
    company_phone_number?: string;
    website?: string;
    logo_url?: string;
  };
}

const SignaturePreview: React.FC<SignaturePreviewProps> = ({
  userProfile,
  company,
}) => {
  const { profile } = useAuth();

  // Fetch company's email signature template from document_defaults
  const { data: documentDefaults, isLoading } = useQuery({
    queryKey: ['document-defaults-signature', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return null;
      const { data, error } = await supabase
        .from('document_defaults')
        .select('email_signature_template')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id,
  });

  // Build dynamic data from user profile and company
  const dynamicData: SignatureDynamicData = {
    user: {
      first_name: userProfile.first_name,
      last_name: userProfile.last_name,
      email: userProfile.email,
      personal_phone_number: userProfile.personal_phone_number,
      company_position: userProfile.company_position,
    },
    company: {
      name: company?.name,
      email: company?.email,
      company_phone_number: company?.company_phone_number,
      website: company?.website,
    },
  };

  // Parse the template and render HTML with absolute URLs for email compatibility
  const signatureHtml = React.useMemo(() => {
    if (!documentDefaults?.email_signature_template) return null;
    
    const gridData = jsonToGridData(documentDefaults.email_signature_template);
    if (!gridData) return null;
    
    // Use window.location.origin as baseUrl for absolute icon paths
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return gridToHtml(gridData, dynamicData, { baseUrl });
  }, [documentDefaults?.email_signature_template, dynamicData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // No template configured
  if (!signatureHtml) {
    return (
      <Card className="border-muted">
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center text-muted-foreground">
            <Info className="h-10 w-10 mb-3 text-muted-foreground/50" />
            <p className="font-medium">No email signature template configured</p>
            <p className="text-sm mt-1">
              Contact your company administrator to set up the email signature template.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Info className="h-4 w-4" />
        <span>This signature uses your company template and is automatically updated with your profile information.</span>
      </div>
      
      <Card>
        <CardContent className="p-4">
          <div
            className="signature-preview"
            dangerouslySetInnerHTML={{ __html: signatureHtml }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SignaturePreview;
